import { Link } from '@tanstack/react-router';
import { getNameFa } from '@/lib/entity-names';
import { useUiStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

/**
 * <EntityName> — bilingual entity display (frontend spec §4.5, §7).
 * Always renders canonical + foreign form together when available.
 * In en mode: "Trita Parsi · تریتا پارسی"
 * In fa mode: "تریتا پارسی · Trita Parsi"
 * The foreign script is rendered at 0.85× and muted.
 *
 * When `resolutionConfidence` is supplied and below 1.0, a small amber
 * pill appears indicating the entity was resolved with uncertainty —
 * the UI's honest acknowledgement that "T. Parsi" in a 2001 cable may
 * not be Trita Parsi (frontend spec §5.4, §6.3 region 1, §7 bullet for
 * EntityName: "handles resolution confidence indicator").
 */
interface EntityNameProps {
  name: string;
  nameFa?: string;
  /** When true, render as a TanStack router <Link> to /entity/$name. */
  link?: boolean;
  asHeading?: boolean;
  hideForeign?: boolean;
  /**
   * 0.0–1.0 resolution confidence. Omit when the match is certain
   * (1.0 — canonical). When <1.0, shows a small indicator pill.
   */
  resolutionConfidence?: number;
  className?: string;
}

export function EntityName({
  name,
  nameFa,
  link,
  asHeading,
  hideForeign,
  resolutionConfidence,
  className,
}: EntityNameProps) {
  const lang = useUiStore((s) => s.lang);
  const fa = nameFa ?? getNameFa(name);

  const primary = lang === 'fa' && fa ? fa : name;
  const primaryLang = lang === 'fa' && fa ? 'fa' : 'en';
  const secondary = lang === 'fa' && fa ? name : fa;
  const secondaryLang = lang === 'fa' && fa ? 'en' : 'fa';

  const showResolutionPill =
    resolutionConfidence != null && resolutionConfidence < 1;

  const content = (
    <span className={cn('inline-flex items-baseline gap-1.5', className)}>
      <bdi lang={primaryLang} className={asHeading ? 'font-serif' : undefined}>
        {primary}
      </bdi>
      {!hideForeign && secondary && (
        <bdi
          lang={secondaryLang}
          className="text-[0.85em] text-[var(--color-ink-muted)]"
        >
          · {secondary}
        </bdi>
      )}
      {showResolutionPill && (
        <span
          className="ms-0.5 rounded-xs bg-[var(--color-warning-soft)] px-1 font-mono text-[9px] uppercase tracking-wider text-[var(--color-warning)]"
          title={`Resolution confidence ${resolutionConfidence!.toFixed(2)} — review before citing.`}
          aria-label={`Entity resolution confidence ${resolutionConfidence!.toFixed(2)}`}
        >
          ~{resolutionConfidence!.toFixed(2)}
        </span>
      )}
    </span>
  );

  if (link) {
    return (
      <Link
        to="/entity/$name"
        params={{ name }}
        className="underline-offset-2 hover:underline"
      >
        {content}
      </Link>
    );
  }
  return content;
}
